import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  slugFr: string | null;
  slugEn: string | null;
  excerpt: string;
  content: string;
  coverImage: string;
  author: string;
  publishedDate: string;
  tags: string[];
}

async function fetchBlogPosts(language: 'fr' | 'en'): Promise<BlogPost[]> {
  const { data, error } = await supabase.functions.invoke('fetch-blog', {
    method: 'POST',
    body: { language },
  });

  if (error) {
    console.error('Error fetching blog posts:', error);
    throw new Error('Failed to fetch blog posts');
  }

  return data?.posts || [];
}

async function fetchBlogPost(slug: string, language: 'fr' | 'en'): Promise<BlogPost | null> {
  const { data, error } = await supabase.functions.invoke('fetch-blog', {
    method: 'POST',
    body: { slug, language },
  });

  if (error) {
    console.error('Error fetching blog post:', error);
    throw new Error('Failed to fetch blog post');
  }

  const posts = data?.posts || [];
  return posts.length > 0 ? posts[0] : null;
}

export function useBlogPosts() {
  const { language } = useLanguage();
  
  return useQuery({
    queryKey: ['blog-posts', language],
    queryFn: () => fetchBlogPosts(language),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useBlogPost(slug: string) {
  const { language } = useLanguage();
  
  return useQuery({
    queryKey: ['blog-post', slug, language],
    queryFn: () => fetchBlogPost(slug, language),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!slug,
  });
}
